import { usePlanningStore } from '@planning/AppProvider'
import { NumericReleaseDetailsViewStore } from '@planning/pages/Releases/Stores/NumericReleaseDetailsViewStore'
import { INumericReleaseItem } from '@planning/rt-stores/numericRelease/NumericReleaseItem'
import { IOrderItem } from '@planning/rt-stores/order/OrderItem'
import numericReleaseService from '@planning/services/numericReleaseService'
import { useTranslate } from '@tolgee/react'
import { ButtonOption, OptionsButton } from '@tom-ui/ui'
import { PATHS } from '@tom-ui/utils'
import { MRT_Row } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { ReleaseDetailsTable } from '../../molecules/ReleaseDetailsTable'
import { NumericReleaseAssignOrderDialog } from './NumericReleaseAssignOrderDialog'
interface Props {
  viewStore: NumericReleaseDetailsViewStore
  numericRelease: INumericReleaseItem
}

export const NumericReleaseDetailsTable = observer((props: Props) => {
  const { numericRelease, viewStore } = props
  const { appViewStore } = usePlanningStore()
  const { t } = useTranslate()
  const navigate = useNavigate()
  const location = useLocation()

  const onConfirmDelete = useCallback(
    async (orderId: number) => {
      const confirm = await appViewStore.setOpenConfirmDialog(
        true,
        t('confirmDelete', 'Are you sure you want to unassign this container?'),
        t('unassignContainer', 'Unassign Container'),
        undefined,
        {
          primaryActionText: t('yesUnassign', 'Yes, unassign'),
          secondaryActionText: t('noCancel', 'No, cancel'),
        },
      )

      if (confirm) {
        try {
          await numericReleaseService.unassignContainer({ orderId })
          appViewStore.setShowAlert('success', t('containerUnassigned', 'Container unassigned'))
        } catch (error) {
          appViewStore.setShowAlert(
            'error',
            t('failedToUnassignContainer', 'Failed to unassign container'),
          )
        }
      }
    },
    [appViewStore, t],
  )

  const options = useCallback(
    (row: MRT_Row<IOrderItem>) => {
      const orderItem = row.original
      const id = orderItem.data.id
      const releaseData = numericRelease.data

      const options: ButtonOption[] = [
        {
          label: t('view', 'View'),
          onClick: () =>
            navigate(`${PATHS.visits.containerVisits.url}/order/${id}`, {
              state: {
                customBreadcrumbs: [
                  {
                    label: t('numericReleases', 'Numeric releases'),
                    path: PATHS.orders.numericReleases.url,
                  },
                  {
                    label: `${releaseData.shippingLineName}-${releaseData.referenceNumber}`,
                    path: location.pathname,
                  },
                ],
              },
            }),
        },
        {
          label: t('unassign', 'Unassign'),
          disabled: !orderItem.canUnassignFromRelease,
          onClick: () => onConfirmDelete(id),
        },
      ]
      return options
    },
    [numericRelease.data, t, navigate, location.pathname, onConfirmDelete],
  )

  if (!numericRelease) return <></>

  const sx = numericRelease.isExpired
    ? { maxHeight: 'calc(100vh - 390px)', minHeight: 'calc(100vh - 390px)' }
    : { maxHeight: 'calc(100vh - 320px)', minHeight: 'calc(100vh - 320px)' }

  return (
    <>
      <ReleaseDetailsTable
        items={viewStore.orders}
        viewStore={viewStore}
        sx={sx}
        renderRowActions={({ row }) => <OptionsButton options={options(row)} position='right' />}
      />
      <NumericReleaseAssignOrderDialog
        isOpen={viewStore.isAssignDialogOpen}
        onClose={() => {
          viewStore.setIsAssignDialogOpen(false)
        }}
        numericRelease={numericRelease.data}
      />
    </>
  )
})
